import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CloseOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Typography,
} from '@mui/material';

import { DialogType } from '@containers/customer/smart-energy/add-installation-dialog';
import {
    useDeleteGatewayInstallation,
    useDeleteHeatPumpInstallation,
    useDeletePvBatteryInstallation,
    useDeleteSmartMeterInstallation,
    useDeleteWallboxInstallation,
} from '@queries/customers';
import { useDeleteUnallocatedInstallation } from '@queries/unallocated-installations';
import { handleError } from '@tools/utils/debug';

import styles from '@containers/customer/smart-energy/index.module.scss';

type DeleteDialogProps = {
    installationType: DialogType | 'unallocated-installation';
    customerId: string;
    installationId: string;
    vendorName?: string;
    handleClose: () => void;
};

const getQuery = (dialogType: DeleteDialogProps['installationType']) => {
    switch (dialogType) {
        case 'meter':
            return useDeleteSmartMeterInstallation;
        case 'gateway':
            return useDeleteGatewayInstallation;
        case 'wallbox':
            return useDeleteWallboxInstallation;
        case 'pvBattery':
            return useDeletePvBatteryInstallation;
        case 'unallocated-installation':
            return useDeleteUnallocatedInstallation;
        case 'heatpump':
            return useDeleteHeatPumpInstallation;
        default:
            return useDeletePvBatteryInstallation;
    }
};

const RemoveInstallationDialog: React.FC<DeleteDialogProps> = ({
    installationType,
    customerId,
    installationId,
    vendorName,
    handleClose,
}) => {
    const { t } = useTranslation();
    const [pageError, setPageError] = useState('');

    const query = getQuery(installationType);
    const { mutateAsync: removeDevice, isPending: isLoading } =
        query(customerId);

    const handleRemove = useCallback(async () => {
        try {
            setPageError('');
            await removeDevice({ configurationId: installationId });

            handleClose();
        } catch (e) {
            const err = await handleError(e);

            setPageError(err);
        }
    }, [removeDevice, installationId, handleClose]);

    return (
        <Dialog
            open
            fullWidth
            onClick={(e) => e.stopPropagation()}
            onClose={handleClose}
            maxWidth="sm"
            className={styles.dialog}
            data-testid="remove-dialog"
            sx={
                isLoading
                    ? {
                          '.MuiPaper-root': {
                              padding: 0,
                          },
                      }
                    : {}
            }
        >
            {isLoading ? (
                <LinearProgress sx={{ m: 0 }} color="primary" />
            ) : null}
            <Box className={styles.dialogHeader}>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    className={styles.iconButton}
                    disabled={isLoading}
                >
                    <CloseOutlined />
                </IconButton>

                <DialogTitle>
                    {t('smartEnergyDevicesTab.removeInstallation.title')}
                </DialogTitle>
            </Box>
            {!isLoading ? (
                <DialogContent className={styles.content}>
                    <DialogContentText>
                        {t(
                            'smartEnergyDevicesTab.removeInstallation.subtitle',
                            {
                                name: vendorName ?? '',
                            },
                        )}
                    </DialogContentText>
                    {pageError && (
                        <Typography className={styles.error}>
                            {t(
                                'smartEnergyDevicesTab.removeInstallation.error',
                                {
                                    errorMessage: pageError,
                                },
                            )}
                        </Typography>
                    )}
                    <Box className={styles.actions}>
                        <Button
                            sx={{ mx: 2 }}
                            variant="contained"
                            className={styles.cancelButton}
                            onClick={handleClose}
                        >
                            {t('common.no')}
                        </Button>
                        <Button
                            sx={{ mx: 2 }}
                            variant="contained"
                            onClick={handleRemove}
                        >
                            {t(
                                'smartEnergyDevicesTab.removeInstallation.button',
                            )}
                        </Button>
                    </Box>
                </DialogContent>
            ) : (
                <Box
                    sx={{
                        paddingBottom: 3,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        {t(
                            'smartEnergyDevicesTab.removeInstallation.submitting',
                        )}
                    </Typography>
                </Box>
            )}
        </Dialog>
    );
};

export default RemoveInstallationDialog;
